import * as React from 'react';
import { LoadingSpinner } from '../LoadingSpinner';
import { type ISearchFilterProp, SearchFilter } from './SearchFilter';
import { type ISearchListProps, SearchList } from './SearchList';
import type { FilterItem, SearchResult } from './Types';

export interface ISearchPageProp {
    filter: ISearchFilterProp;
    list: ISearchListProps;
    items: SearchResult[];
    region: string;
}

interface ISearchPageState {
    selectedfilters: number[];
    displayitems: SearchResult[];
    searchtext: string;
    dateto: string;
    datefrom: string;
    loading: boolean;
}

export class SearchPage extends React.Component<ISearchPageProp, ISearchPageState> {
    private filtertimeout;
    private items: SearchResult[];
    private timeoutId;
    private lastSearch;

    private readonly defaultFilter = {
        searchtext: '',
        dateto: '',
        datefrom: '',
        selectedfilters: [0, 1, 2, 4, 5, 6],
    };

    constructor(props: ISearchPageProp) {
        super(props);
        const state = {
            ...JSON.parse(JSON.stringify(this.defaultFilter)),
            displayitems: [],
        };
        state.searchtext = this.props.filter.searchtext;
        this.items = this.props.items;

        state.displayitems = this.filterlist(state.selectedfilters, state.datefrom, state.dateto);
        this.state = state;
    }

    render(): JSX.Element {
        return (
            <div className="row">
                {this.state.loading && <LoadingSpinner />}
                <div className="col-12 col-md-4 col-lg-3">
                    <SearchFilter
                        selectedfilters={this.state.selectedfilters}
                        addfilter={this.addfilter}
                        removefilter={this.removefilter}
                        clearfilter={this.clearfilter}
                        dateto={this.state.dateto}
                        datefrom={this.state.datefrom}
                        searchtext={this.state.searchtext}
                        onupdate={this.onUpdate}
                        region={this.props.region}
                        className="mb-4 mb-md-0"
                    />
                </div>
                <div className="col-12 col-md-8 col-lg-9">
                    {this.showHelp(this.state.selectedfilters) ? this.renderHelp : <SearchList items={this.state.displayitems} />}
                </div>
            </div>
        );
    }

    private search() {
        this.setState({
            loading: true,
        });
        fetch('/api/search?query=' + this.state.searchtext + '&region=' + this.props.region)
            .then((response) => {
                if (response.status === 200) {
                    return response.json();
                }
            })
            .then((data: SearchResult[]) => {
                this.items = data;
                clearTimeout(this.filtertimeout);
                this.filtertimeout = setTimeout(this.filterItems.bind(this), 300);
                const newUrl = this.updateQueryStringParameter(this.state.searchtext);
                history.replaceState({}, 'Searched', newUrl);
            });
    }
    private updateQueryStringParameter(value) {
        const key = 'query';
        const url = window.location.href;

        const re = new RegExp('([?&])' + key + '=.*?(&|$)', 'i');
        const separator = url.indexOf('?') !== -1 ? '&' : '?';
        if (url.match(re)) {
            return url.replace(re, '$1' + key + '=' + value + '$2');
        } else {
            return url + separator + key + '=' + value;
        }
    }

    private showHelp(ids: number[]): boolean {
        const filterdIds = [];

        for (const id of ids) {
            const filter = SearchFilter.GetFilterById(id);

            if (filter !== undefined) {
                filterdIds.push(id);
            }
        }

        return filterdIds.length === 0;
    }

    private renderHelp() {
        return (
            <div className="search-page__help">
                <div className="d-md-none">
                    <span className="icon-info-circled" />
                    <span>Välj urval ovan.</span>
                </div>
                <div className="d-none d-md-block">
                    <span className="icon-info-circled" />
                    <span>Välj urval till vänster.</span>
                </div>
            </div>
        );
    }

    private onUpdate = (data) => {
        this.setState(data);

        clearTimeout(this.timeoutId);
        if (data.datefrom !== undefined || data.dateto !== undefined) {
            this.timeoutId = setTimeout(this.filterItems.bind(this), 200);
        } else {
            this.timeoutId = setTimeout(() => {
                if (this.lastSearch !== this.state.searchtext) {
                    this.lastSearch = this.state.searchtext;
                    this.search();
                }
            }, 500);
        }
    };

    private clearfilter = () => {
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const { searchtext, ...filterWithoutSearch } = this.defaultFilter;
        this.setState(JSON.parse(JSON.stringify(filterWithoutSearch)));
        clearTimeout(this.filtertimeout);
        this.filtertimeout = setTimeout(this.filterItems.bind(this), 200);
    };

    private addfilter = (filterId) => {
        const filters = this.state.selectedfilters;
        filters.push(filterId);
        this.setState({ selectedfilters: filters });
        clearTimeout(this.filtertimeout);
        this.filtertimeout = setTimeout(this.filterItems.bind(this), 200);
    };

    private removefilter = (filterId) => {
        const filters = this.state.selectedfilters;
        const index = filters.findIndex((x) => x === filterId);
        filters.splice(index, 1);
        this.setState({ selectedfilters: filters });
        clearTimeout(this.filtertimeout);
        this.filtertimeout = setTimeout(this.filterItems.bind(this), 200);
    };

    private filterByCategories(selectedFilter: FilterItem[], filters: FilterItem[], items: SearchResult[]): SearchResult[] {
        let filtervalues = [];

        for (const filter of filters) {
            if (selectedFilter.some((x) => x.id === filter.id)) {
                filtervalues = filtervalues.concat(filter.value);
            }
        }

        return this.filterByCategory(filtervalues, items);
    }

    private filterByCategory(filters: string[], items: SearchResult[]): SearchResult[] {
        items = items.filter((item: SearchResult) => {
            if (filters.indexOf(item.typeName.toLowerCase()) > -1) {
                return true;
            }
            return false;
        });

        return items;
    }

    private filterByFromDate(datefrom: Date, items: SearchResult[]): SearchResult[] {
        items = items.filter((item: SearchResult) => {
            if (item.startTime) {
                const situationStart = new Date(item.startTime);
                return situationStart >= datefrom || !item.endTime || new Date(item.endTime) >= datefrom;
            } else if (item.publishDate) {
                const publishDate = new Date(item.publishDate);
                const updateDate = item.updateDate && new Date(item.updateDate);
                return publishDate >= datefrom || updateDate >= datefrom;
            }
            return true;
        });

        return items;
    }

    private filterByToDate(dateto: Date, items: SearchResult[]): SearchResult[] {
        items = items.filter((item: SearchResult) => {
            if (item.typeName === 'SituationSearchModel') {
                const situationStart = new Date(item.startTime);
                return situationStart <= dateto;
            } else if (item.typeName === 'TAPlanSearchModel') {
                const situationStart = new Date(item.startTime);
                return situationStart <= dateto;
            } else if (item.typeName === 'NewsArticlePage') {
                const publishDate = new Date(item.publishDate);
                return publishDate <= dateto;
            }
            return true;
        });

        return items;
    }

    private filterlist(selectedfilters: number[], datefrom: string, dateto: string): SearchResult[] {
        let result: SearchResult[] = this.items;

        const filters: FilterItem[] = [];

        for (const selectedFilter of selectedfilters) {
            filters.push(SearchFilter.GetFilterById(selectedFilter));
        }

        if (filters.length > 0) {
            result = this.filterByCategories(filters, filters, result);
        }

        if (datefrom) {
            result = this.filterByFromDate(new Date(datefrom), result);
        }
        if (dateto) {
            result = this.filterByToDate(new Date(dateto), result);
        }

        return result;
    }

    private filterItems() {
        const result = this.filterlist(this.state.selectedfilters, this.state.datefrom, this.state.dateto);
        this.setState({
            displayitems: result,
            loading: false,
        });
    }
}
